import { useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { capabilityForPath } from '@/lib/studio/navAccess';
import {
  useCapabilityKeys,
  useIsAuthorizationResolved,
} from '@/hooks/auth/useCapabilities';

/**
 * Route-level capability authorization.
 *
 * WHAT THIS CLOSES. Capability gating hid sidebar links and nothing else — `routes/config.ts` carries
 * no capability field and `ProtectedRoute` checks authentication only — so every gated module was
 * reachable by typing its URL, following a stale deep link, or a `navigate()` from anywhere. Hiding a
 * link is discoverability; this is authorization.
 *
 * IT DECIDES BEFORE THE MODULE MOUNTS. `children` is a React element, not a rendered component: if
 * this returns anything else, the module never mounts and therefore never fires a query. That is the
 * difference between "authorized" and "loaded, then hidden after the requests went out" — the latter
 * leaks data over the network and leaves the user watching error states.
 *
 * IT EXPLAINS RATHER THAN REDIRECTS, following the pattern the org-admin panels already set: "rather
 * than hide the tab — which makes a shared link dead-end silently — the panel says what is missing and
 * why." A redirect to the dashboard would answer a colleague's shared link with a screen that looks
 * like the link was wrong. And because capabilities are per connection, the same URL may be authorized
 * on a different one — which the copy says, since switching is the actual remedy.
 *
 * IT IS NOT THE LAST LINE OF DEFENCE, and must not be treated as one. The capability list is data the
 * client was handed; the backend proxy authorises every module call and 403s independently. This stops
 * an unauthorized module from mounting and fetching. It does not stop a determined client.
 */

export interface RequireCapabilityProps {
  children: React.ReactNode;
}

export function RequireCapability({ children }: RequireCapabilityProps) {
  const location = useLocation();
  const capabilityKeys = useCapabilityKeys();
  const isResolved = useIsAuthorizationResolved();
  const orgName = useAuthStoreV1((s) => s.selectedOrg?.orgName ?? null);
  const hasAnyConnection = useAuthStoreV1((s) => s.organizations.length > 0);

  const required = capabilityForPath(location.pathname);

  // Ungated route. Most of the app: Home, Deployment, Observability, the admin panel, auth screens.
  if (required === null) return <>{children}</>;

  /*
   * Authorization not yet decidable.
   *
   * An empty capability list during the initial user fetch denies everything, so rendering a decision
   * here would flash "not available" on a module the user owns. Holding the route — rather than
   * optimistically rendering the module — is also what stops a direct URL racing ahead of the check:
   * the module cannot mount before the answer exists.
   */
  if (!isResolved) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="flex min-h-[60vh] items-center justify-center"
      >
        <span className="sr-only">Checking access…</span>
      </div>
    );
  }

  if (capabilityKeys.includes(required)) return <>{children}</>;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 grid h-11 w-11 place-items-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-muted-foreground" aria-hidden />
        </div>
        {/* `h1`: this is the page now, not a section of one. */}
        <h1 className="text-base font-semibold text-foreground">
          This module isn’t enabled for you here
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
          {/*
            Names the CONNECTION, because that is the whole subtlety: access is granted per connected
            Salesforce environment, so the same link can work elsewhere. Telling someone they lack
            access without saying "here" sends them to ask for a permission they may already have.
          */}
          {hasAnyConnection && orgName ? (
            <>
              Your role on <span className="font-medium text-foreground">{orgName}</span> doesn’t
              include this module. It may be available on another connection — try switching, or ask
              an owner to add it to your role.
            </>
          ) : (
            <>
              Your role doesn’t include this module. Ask an owner to add it to your role.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
